(() => {
  const countdownTimers = new WeakMap();
  const railStates = new WeakMap();
  const announcementStates = new WeakMap();
  const lookbookStates = new WeakMap();
  const imageDialogStates = new WeakMap();
  const copyCodeStates = new WeakMap();
  let headerObserver;
  let footerMenuMediaQuery;
  let footerOfferObserver;
  let activeSearchTrigger;
  const navigationHoverMedia = window.matchMedia('(hover: hover) and (pointer: fine)');
  const navigationCloseTimers = new WeakMap();
  let navigationKeyboardInput = false;

  const cancelNavigationClose = (details) => {
    window.clearTimeout(navigationCloseTimers.get(details));
    navigationCloseTimers.delete(details);
  };

  const hasNavigationKeyboardFocus = (details) => navigationKeyboardInput && details.contains(document.activeElement);

  const scheduleNavigationClose = (details) => {
    cancelNavigationClose(details);
    navigationCloseTimers.set(details, window.setTimeout(() => {
      navigationCloseTimers.delete(details);
      if (!details.matches(':hover') && !hasNavigationKeyboardFocus(details)) details.open = false;
    }, 220));
  };

  document.addEventListener('pointerdown', () => {
    navigationKeyboardInput = false;
  }, { passive: true });

  document.addEventListener('pointerover', (event) => {
    if (!navigationHoverMedia.matches || event.pointerType !== 'mouse' || !(event.target instanceof Element)) return;
    const details = event.target.closest('.thb-header__nav-details');
    if (!(details instanceof HTMLDetailsElement) || (event.relatedTarget instanceof Node && details.contains(event.relatedTarget))) return;
    cancelNavigationClose(details);
    const siblings = details.closest('.thb-header__nav-list')?.querySelectorAll('.thb-header__nav-details[open]') || [];
    if ([...siblings].some((sibling) => sibling !== details && hasNavigationKeyboardFocus(sibling))) return;
    siblings.forEach((sibling) => {
      if (sibling !== details) {
        cancelNavigationClose(sibling);
        sibling.open = false;
      }
    });
    details.open = true;
  }, { passive: true });

  document.addEventListener('pointerout', (event) => {
    if (!navigationHoverMedia.matches || event.pointerType !== 'mouse' || !(event.target instanceof Element)) return;
    const details = event.target.closest('.thb-header__nav-details');
    if (!(details instanceof HTMLDetailsElement) || (event.relatedTarget instanceof Node && details.contains(event.relatedTarget))) return;
    scheduleNavigationClose(details);
  }, { passive: true });

  document.addEventListener('focusout', (event) => {
    if (!(event.target instanceof Element)) return;
    const details = event.target.closest('.thb-header__nav-details[open]');
    if (!(details instanceof HTMLDetailsElement) || (event.relatedTarget instanceof Node && details.contains(event.relatedTarget))) return;
    scheduleNavigationClose(details);
  });

  const clearCountdown = (countdown) => {
    const timer = countdownTimers.get(countdown);
    if (timer) window.clearInterval(timer);
    countdownTimers.delete(countdown);
    delete countdown.dataset.thbReady;
  };

  const renderCountdownFallback = (countdown, container) => {
    const fallback = container.querySelector('template[data-thb-countdown-fallback]');
    if (!(fallback instanceof HTMLTemplateElement)) return false;
    const fallbackContent = fallback.content.cloneNode(true);
    clearCountdown(countdown);
    container.replaceChildren(fallbackContent);
    container.removeAttribute('data-thb-countdown');
    if (container.dataset.thbCountdownFallbackLabel) {
      container.setAttribute('aria-label', container.dataset.thbCountdownFallbackLabel);
    }
    container.hidden = false;
    return true;
  };

  const initializeCountdown = (countdown) => {
    if (countdown.dataset.thbReady === 'true') return;
    const output = countdown.querySelector('[data-thb-countdown-output]');
    const target = Date.parse(countdown.dataset.thbCountdown || '');
    const container = countdown.closest('[data-thb-countdown-container]') || countdown;
    if (!output || Number.isNaN(target) || target <= Date.now()) {
      if (!renderCountdownFallback(countdown, container)) {
        container.hidden = true;
        clearCountdown(countdown);
      }
      return;
    }

    countdown.dataset.thbReady = 'true';
    container.hidden = false;
    const update = () => {
      const distance = Math.max(0, target - Date.now());
      const days = Math.floor(distance / 86400000);
      const hours = Math.floor(distance / 3600000) % 24;
      const minutes = Math.floor(distance / 60000) % 60;
      const seconds = Math.floor(distance / 1000) % 60;
      output.textContent = [days, hours, minutes, seconds]
        .map((value) => String(value).padStart(2, '0'))
        .join(' : ');
      if (distance === 0) {
        if (!renderCountdownFallback(countdown, container)) {
          container.hidden = true;
          clearCountdown(countdown);
        }
      }
    };

    update();
    if (target > Date.now()) countdownTimers.set(countdown, window.setInterval(update, 1000));
  };

  const updateHeaderMeasurements = () => {
    const header = document.querySelector('.thb-header');
    const headerGroup = document.querySelector('#header-group');
    if (!header || !headerGroup) return;
    document.body.style.setProperty('--header-height', `${Math.round(header.getBoundingClientRect().height)}px`);
    document.body.style.setProperty('--header-group-height', `${Math.round(headerGroup.getBoundingClientRect().height)}px`);
  };

  const observeHeader = () => {
    headerObserver?.disconnect();
    updateHeaderMeasurements();
    if (!('ResizeObserver' in window)) return;
    const headerGroup = document.querySelector('#header-group');
    if (!headerGroup) return;
    headerObserver = new ResizeObserver(updateHeaderMeasurements);
    headerObserver.observe(headerGroup);
  };

  const activateLookProduct = (lookbook, blockId) => {
    if (!(lookbook instanceof HTMLElement) || !blockId) return;
    const hotspots = lookbook.querySelectorAll('[data-thb-look-hotspot]');
    const cards = lookbook.querySelectorAll('[data-thb-look-card]');

    hotspots.forEach((hotspot) => {
      const isActive = hotspot.dataset.thbLookHotspot === blockId;
      hotspot.setAttribute('aria-pressed', String(isActive));
    });

    cards.forEach((card) => {
      card.hidden = card.dataset.thbLookCard !== blockId;
    });
  };

  const initializeLookbook = (lookbook) => {
    if (!(lookbook instanceof HTMLElement) || lookbookStates.has(lookbook)) return;
    const hotspots = [...lookbook.querySelectorAll('[data-thb-look-hotspot]')];
    if (hotspots.length === 0) return;

    const controller = new AbortController();
    lookbookStates.set(lookbook, { controller });
    hotspots.forEach((hotspot) => {
      hotspot.addEventListener('click', () => {
        activateLookProduct(lookbook, hotspot.dataset.thbLookHotspot);
      }, { signal: controller.signal });
    });

    const initiallyActive = hotspots.find((hotspot) => hotspot.getAttribute('aria-pressed') === 'true') || hotspots[0];
    activateLookProduct(lookbook, initiallyActive.dataset.thbLookHotspot);
    lookbook.dataset.thbLookbookReady = 'true';
  };

  const destroyLookbook = (lookbook) => {
    const state = lookbookStates.get(lookbook);
    state?.controller.abort();
    lookbookStates.delete(lookbook);
    delete lookbook.dataset.thbLookbookReady;
  };

  const restoreImageDialogFocus = (state) => {
    const trigger = state.lastTrigger;
    state.lastTrigger = undefined;
    if (
      trigger instanceof HTMLElement
      && document.contains(trigger)
      && trigger.getClientRects().length > 0
    ) {
      trigger.focus({ preventScroll: true });
    }
  };

  const resetImageDialog = (state) => {
    state.image.removeAttribute('src');
    state.image.alt = '';
  };

  const closeImageDialog = (state) => {
    if (state.dialog.open) state.dialog.close();
  };

  const syncImageDialogScrollLock = () => {
    const hasOpenImageDialog = Boolean(document.querySelector('[data-thb-image-dialog][open]'));
    document.documentElement.toggleAttribute('data-thb-image-dialog-open', hasOpenImageDialog);
  };

  const initializeImageDialog = (home) => {
    if (!(home instanceof HTMLElement) || imageDialogStates.has(home)) return;
    const dialog = home.querySelector('[data-thb-image-dialog]');
    const image = dialog?.querySelector('[data-thb-image-dialog-image]');
    const closeButton = dialog?.querySelector('[data-thb-image-dialog-close]');
    const triggers = Array.from(home.querySelectorAll('[data-thb-image-zoom]'));
    if (
      !(dialog instanceof HTMLDialogElement)
      || !(image instanceof HTMLImageElement)
      || !(closeButton instanceof HTMLElement)
      || triggers.length === 0
    ) return;

    const controller = new AbortController();
    const state = { closeButton, controller, dialog, image, lastTrigger: undefined };
    imageDialogStates.set(home, state);
    dialog.dataset.thbImageDialogReady = 'true';

    triggers.forEach((trigger) => {
      if (!(trigger instanceof HTMLElement)) return;
      trigger.addEventListener('click', (event) => {
        if (
          event instanceof MouseEvent
          && (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
        ) return;
        const source = trigger.dataset.thbZoomSrc?.trim();
        if (!source || typeof dialog.showModal !== 'function') return;

        event.preventDefault();
        state.lastTrigger = trigger;
        image.src = source;
        image.alt = trigger.dataset.thbZoomAlt || '';
        if (!dialog.open) dialog.showModal();
        syncImageDialogScrollLock();
        closeButton.focus({ preventScroll: true });
      }, { signal: controller.signal });
    });

    closeButton.addEventListener('click', (event) => {
      event.preventDefault();
      closeImageDialog(state);
    }, { signal: controller.signal });

    dialog.addEventListener('click', (event) => {
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      const isBackdropClick = (
        event.clientX < bounds.left
        || event.clientX > bounds.right
        || event.clientY < bounds.top
        || event.clientY > bounds.bottom
      );
      if (isBackdropClick) closeImageDialog(state);
    }, { signal: controller.signal });

    dialog.addEventListener('close', () => {
      syncImageDialogScrollLock();
      resetImageDialog(state);
      restoreImageDialogFocus(state);
    }, { signal: controller.signal });
  };

  const destroyImageDialog = (home) => {
    const state = imageDialogStates.get(home);
    if (!state) return;
    closeImageDialog(state);
    syncImageDialogScrollLock();
    resetImageDialog(state);
    restoreImageDialogFocus(state);
    state.controller.abort();
    delete state.dialog.dataset.thbImageDialogReady;
    imageDialogStates.delete(home);
  };

  const copyWithTextarea = (text) => {
    const textarea = document.createElement('textarea');
    const activeElement = document.activeElement;
    const selection = document.getSelection();
    const ranges = selection
      ? Array.from({ length: selection.rangeCount }, (_item, index) => selection.getRangeAt(index).cloneRange())
      : [];

    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.setAttribute('aria-hidden', 'true');
    textarea.tabIndex = -1;
    textarea.style.position = 'fixed';
    textarea.style.inset = '0 auto auto -9999px';
    textarea.style.opacity = '0';
    document.body.append(textarea);

    let copied = false;
    try {
      textarea.select();
      textarea.setSelectionRange(0, textarea.value.length);
      copied = document.execCommand('copy');
    } finally {
      textarea.remove();
      if (selection) {
        selection.removeAllRanges();
        ranges.forEach((range) => selection.addRange(range));
      }
      if (activeElement instanceof HTMLElement && document.contains(activeElement)) {
        activeElement.focus({ preventScroll: true });
      }
    }
    if (!copied) throw new Error('Copy command was not accepted');
  };

  const writeToClipboard = async (text) => {
    if (window.isSecureContext && typeof navigator.clipboard?.writeText === 'function') {
      try {
        await navigator.clipboard.writeText(text);
        return;
      } catch (_error) {
        copyWithTextarea(text);
        return;
      }
    }
    copyWithTextarea(text);
  };

  const findCopyStatus = (button) => {
    const containedStatus = button.querySelector('[data-thb-copy-status]');
    if (containedStatus instanceof HTMLElement) return containedStatus;

    const describedIds = (button.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
    for (const id of describedIds) {
      const described = document.getElementById(id);
      if (described instanceof HTMLElement && described.matches('[data-thb-copy-status]')) return described;
    }

    const siblingStatus = button.parentElement?.querySelector(':scope > [data-thb-copy-status]');
    return siblingStatus instanceof HTMLElement ? siblingStatus : undefined;
  };

  const restoreCopyStatus = (button, state) => {
    if (state.resetTimer) window.clearTimeout(state.resetTimer);
    state.resetTimer = 0;
    delete button.dataset.thbCopyState;
    button.removeAttribute('aria-busy');
    if (state.originalAriaLabel === null) {
      button.removeAttribute('aria-label');
    } else {
      button.setAttribute('aria-label', state.originalAriaLabel);
    }
    state.status.removeAttribute('role');
    state.status.setAttribute('aria-live', 'off');
    state.status.removeAttribute('aria-atomic');
    state.status.textContent = state.originalStatusText;
    ['role', 'aria-live', 'aria-atomic'].forEach((attribute) => {
      const originalValue = state.originalStatusAttributes[attribute];
      if (originalValue === null) {
        state.status.removeAttribute(attribute);
      } else {
        state.status.setAttribute(attribute, originalValue);
      }
    });
  };

  const showCopyStatus = (button, state, statusName) => {
    const isSuccess = statusName === 'success';
    const message = isSuccess
      ? button.dataset.thbCopySuccess || button.dataset.thbCopySuccessLabel || 'Copied'
      : button.dataset.thbCopyError || button.dataset.thbCopyErrorLabel || 'Unable to copy';
    state.status.textContent = message;
    button.dataset.thbCopyState = statusName;
    button.setAttribute('aria-label', message);
    button.removeAttribute('aria-busy');
    if (state.resetTimer) window.clearTimeout(state.resetTimer);
    state.resetTimer = window.setTimeout(() => {
      if (copyCodeStates.get(button) !== state) return;
      restoreCopyStatus(button, state);
      state.status.setAttribute('role', 'status');
      state.status.setAttribute('aria-live', 'polite');
      state.status.setAttribute('aria-atomic', 'true');
    }, 2500);
  };

  const initializeCopyCode = (button) => {
    if (!(button instanceof HTMLButtonElement) || copyCodeStates.has(button)) return;
    let status = findCopyStatus(button);
    const ownsStatus = !(status instanceof HTMLElement);
    if (ownsStatus) {
      status = document.createElement('span');
      status.className = 'thb-visually-hidden';
      status.dataset.thbCopyStatus = '';
      button.append(status);
    }

    const controller = new AbortController();
    const state = {
      controller,
      originalAriaLabel: button.getAttribute('aria-label'),
      originalStatusAttributes: {
        role: status.getAttribute('role'),
        'aria-live': status.getAttribute('aria-live'),
        'aria-atomic': status.getAttribute('aria-atomic'),
      },
      originalStatusText: status.textContent,
      ownsStatus,
      resetTimer: 0,
      status,
    };
    copyCodeStates.set(button, state);
    button.dataset.thbCopyReady = 'true';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('aria-atomic', 'true');

    button.addEventListener('click', async () => {
      const code = button.dataset.thbCode?.trim();
      if (!code) {
        showCopyStatus(button, state, 'error');
        return;
      }
      button.setAttribute('aria-busy', 'true');
      try {
        await writeToClipboard(code);
        if (copyCodeStates.get(button) === state) showCopyStatus(button, state, 'success');
      } catch (_error) {
        if (copyCodeStates.get(button) === state) showCopyStatus(button, state, 'error');
      }
    }, { signal: controller.signal });
  };

  const destroyCopyCode = (button) => {
    const state = copyCodeStates.get(button);
    if (!state) return;
    state.controller.abort();
    restoreCopyStatus(button, state);
    if (state.ownsStatus) state.status.remove();
    copyCodeStates.delete(button);
    delete button.dataset.thbCopyReady;
  };

  const initializeHeaderSearch = (scope = document) => {
    const searchModal = document.getElementById('search-modal');
    const searchDialog = searchModal?.querySelector('#search-modal-dialog');
    if (!(searchModal instanceof HTMLElement) || !(searchDialog instanceof HTMLDialogElement)) return;

    if (searchModal.dataset.thbSearchStateReady !== 'true') {
      searchModal.dataset.thbSearchStateReady = 'true';

      searchModal.addEventListener('dialog:open', () => {
        delete searchModal.dataset.thbSearchOpening;
        const focusedTrigger = document.activeElement?.closest?.('[data-thb-search-trigger]');
        if (focusedTrigger instanceof HTMLElement) activeSearchTrigger = focusedTrigger;
        document.querySelectorAll('[data-thb-search-trigger]').forEach((trigger) => {
          trigger.setAttribute('aria-expanded', String(trigger === activeSearchTrigger));
        });
      });

      searchModal.addEventListener('dialog:close', () => {
        delete searchModal.dataset.thbSearchOpening;
        document.querySelectorAll('[data-thb-search-trigger]').forEach((trigger) => {
          trigger.setAttribute('aria-expanded', 'false');
        });

        const triggerToRestore = activeSearchTrigger;
        activeSearchTrigger = undefined;
        if (!(triggerToRestore instanceof HTMLElement)) return;
        const visibleFallback = Array.from(document.querySelectorAll('[data-thb-search-trigger]'))
          .find((trigger) => trigger instanceof HTMLElement && trigger.getClientRects().length > 0);
        const focusTarget = document.contains(triggerToRestore)
          && triggerToRestore.getClientRects().length > 0
          ? triggerToRestore
          : visibleFallback;
        if (focusTarget instanceof HTMLElement) focusTarget.focus({ preventScroll: true });
      });
    }

    scope.querySelectorAll('[data-thb-search-trigger]').forEach((trigger) => {
      if (!(trigger instanceof HTMLAnchorElement) || trigger.dataset.thbSearchReady === 'true') return;
      trigger.dataset.thbSearchReady = 'true';
      trigger.setAttribute('aria-expanded', 'false');

      trigger.addEventListener('click', (event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (typeof searchModal.showDialog !== 'function' || typeof searchDialog.showModal !== 'function') return;

        event.preventDefault();
        if (searchDialog.open || searchModal.dataset.thbSearchOpening === 'true') return;
        activeSearchTrigger = trigger;
        searchModal.dataset.thbSearchOpening = 'true';
        searchModal.showDialog();
      });
    });
  };

  const selectAnnouncement = (announcement, requestedIndex) => {
    const state = announcementStates.get(announcement);
    if (!state || state.slides.length === 0) return;

    const slideCount = state.slides.length;
    state.index = ((Math.trunc(requestedIndex) % slideCount) + slideCount) % slideCount;
    state.slides.forEach((slide, index) => {
      const isActive = index === state.index;
      slide.hidden = !isActive;
      slide.toggleAttribute('inert', !isActive);
      slide.setAttribute('aria-hidden', String(!isActive));
      if (isActive) {
        slide.setAttribute('aria-current', 'true');
      } else {
        slide.removeAttribute('aria-current');
      }
    });
    announcement.dataset.thbAnnouncementIndex = String(state.index);
  };

  const initializeAnnouncement = (announcement) => {
    if (!(announcement instanceof HTMLElement) || announcementStates.has(announcement)) return;

    const track = announcement.querySelector('[data-thb-announcement-track]');
    const slides = Array.from(announcement.querySelectorAll('[data-thb-announcement-slide]'))
      .filter((slide) => slide instanceof HTMLElement);
    if (!(track instanceof HTMLElement) || slides.length === 0) return;

    const controller = new AbortController();
    const initialIndex = Math.max(0, slides.findIndex((slide) => !slide.hidden));
    const state = { controller, slides, index: initialIndex };
    announcementStates.set(announcement, state);

    const previous = announcement.querySelector('[data-thb-announcement-previous]');
    const next = announcement.querySelector('[data-thb-announcement-next]');
    previous?.addEventListener('click', () => selectAnnouncement(announcement, state.index - 1), {
      signal: controller.signal,
    });
    next?.addEventListener('click', () => selectAnnouncement(announcement, state.index + 1), {
      signal: controller.signal,
    });

    selectAnnouncement(announcement, initialIndex);
    announcement.dataset.thbAnnouncementReady = 'true';
  };

  const destroyAnnouncement = (announcement) => {
    const state = announcementStates.get(announcement);
    state?.controller.abort();
    announcementStates.delete(announcement);
    delete announcement.dataset.thbAnnouncementReady;
  };

  const initializeThb = (scope = document) => {
    scope.querySelectorAll('[data-thb-countdown]').forEach(initializeCountdown);
    scope.querySelectorAll('[data-thb-rail]').forEach(initializeRailControls);
    scope.querySelectorAll('[data-thb-lookbook]').forEach(initializeLookbook);
    scope.querySelectorAll('[data-thb-home]').forEach(initializeImageDialog);
    scope.querySelectorAll('[data-thb-copy-code]').forEach(initializeCopyCode);
    scope.querySelectorAll('[data-thb-announcement]').forEach(initializeAnnouncement);
    initializeHeaderSearch(scope);
    syncFooterMenus();
    observeFooterBreakpoint();
    observeFooterOfferBoundary();
    observeHeader();
    syncModalSemantics();
  };

  const formatMoney = (amount, currency, currencyDisplay = 'symbol') => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency,
        currencyDisplay,
      }).format(Number(amount));
    } catch (_error) {
      return `${currency} ${Number(amount).toFixed(2)}`;
    }
  };

  const updateCartDisplay = (cart) => {
    if (!cart) return;
    const count = Number(cart.totalQuantity || 0);
    const totalAmount = cart.cost?.totalAmount;

    document.querySelectorAll('[data-thb-cart-count]').forEach((element) => {
      element.textContent = String(count);
      element.hidden = count === 0;
    });
    if (totalAmount?.currencyCode) {
      document.querySelectorAll('[data-thb-cart-total]').forEach((element) => {
        const currencyDisplay = element.dataset.thbCurrencyDisplay === 'code' ? 'code' : 'symbol';
        element.textContent = formatMoney(totalAmount.amount, totalAmount.currencyCode, currencyDisplay);
      });
    }
    document.querySelectorAll('[data-thb-cart-trigger]').forEach((trigger) => {
      const cartCountLabel = trigger.dataset.thbCartLabel || 'Cart';
      trigger.setAttribute('aria-label', `${cartCountLabel}: ${count}`);
    });
  };

  const handleCartUpdate = (event) => {
    event.promise?.then(({ cart }) => updateCartDisplay(cart)).catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[thb-cart] Unable to update cart display', error);
    });
  };

  const modalFocusOrigins = new WeakMap();
  const modalInertStates = new WeakMap();
  let savedPageOverflow = '';
  let savedBodyOverflow = '';

  const isModalDetails = (details) => {
    if (!(details instanceof HTMLDetailsElement) || !details.matches('[data-thb-modal-details]')) return false;
    const breakpoint = Number(details.dataset.thbModalBreakpoint || 768);
    return window.matchMedia(`(max-width: ${breakpoint}px)`).matches;
  };

  const modalFocusables = (panel) => Array.from(panel.querySelectorAll(
    'a[href], button:not([disabled]), summary, input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  )).filter((element) => !element.hidden && element.getClientRects().length > 0);

  const setModalTriggerExpanded = (details, expanded) => {
    const summary = details.querySelector(':scope > summary');
    summary?.setAttribute('aria-expanded', String(expanded));
  };

  const setOutsideModalInert = (details, makeInert) => {
    if (!makeInert) {
      const previousStates = modalInertStates.get(details) || [];
      previousStates.forEach(({ element, hadInert }) => {
        if (!hadInert && document.contains(element)) element.removeAttribute('inert');
      });
      modalInertStates.delete(details);
      return;
    }

    if (modalInertStates.has(details)) return;
    const targets = new Set();
    const summary = details.querySelector(':scope > summary');
    if (summary instanceof HTMLElement) targets.add(summary);

    let branch = details;
    while (branch instanceof HTMLElement && branch !== document.body) {
      const parent = branch.parentElement;
      if (!parent) break;
      Array.from(parent.children).forEach((sibling) => {
        if (
          sibling !== branch
          && sibling instanceof HTMLElement
          && !sibling.matches('script, style, link, template')
        ) {
          targets.add(sibling);
        }
      });
      branch = parent;
    }

    const previousStates = Array.from(targets).map((element) => ({
      element,
      hadInert: element.hasAttribute('inert'),
    }));
    previousStates.forEach(({ element }) => element.setAttribute('inert', ''));
    modalInertStates.set(details, previousStates);
  };

  const syncPageScrollLock = () => {
    const hasOpenModal = Array.from(document.querySelectorAll('details[data-thb-modal-details][open]')).some(isModalDetails);
    if (hasOpenModal) {
      if (document.documentElement.dataset.thbScrollLocked !== 'true') {
        savedPageOverflow = document.documentElement.style.overflow;
        savedBodyOverflow = document.body.style.overflow;
        document.documentElement.dataset.thbScrollLocked = 'true';
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
      }
      return;
    }

    if (document.documentElement.dataset.thbScrollLocked === 'true') {
      document.documentElement.style.overflow = savedPageOverflow;
      document.body.style.overflow = savedBodyOverflow;
      delete document.documentElement.dataset.thbScrollLocked;
    }
  };

  const closeModalDetails = (details, restoreFocus = true) => {
    if (!(details instanceof HTMLDetailsElement)) return;
    const origin = modalFocusOrigins.get(details) || details.querySelector(':scope > summary');
    setOutsideModalInert(details, false);
    setModalTriggerExpanded(details, false);
    details.open = false;
    syncPageScrollLock();
    if (!restoreFocus) return;
    if (origin instanceof HTMLElement) origin.focus({ preventScroll: true });
  };

  const handleModalToggle = (details) => {
    const panel = details.querySelector('[data-thb-modal-panel]');
    if (!(panel instanceof HTMLElement)) return;

    if (!isModalDetails(details)) {
      setOutsideModalInert(details, false);
      setModalTriggerExpanded(details, details.open);
      panel.removeAttribute('role');
      panel.removeAttribute('aria-modal');
      syncPageScrollLock();
      return;
    }

    if (details.open) {
      const summary = details.querySelector(':scope > summary');
      modalFocusOrigins.set(details, summary instanceof HTMLElement ? summary : document.activeElement);
      setModalTriggerExpanded(details, true);
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
      setOutsideModalInert(details, true);
      syncPageScrollLock();
      const focusTarget = panel.querySelector('[data-thb-modal-close]') || modalFocusables(panel)[0] || panel;
      if (focusTarget instanceof HTMLElement) focusTarget.focus({ preventScroll: true });
    } else {
      setOutsideModalInert(details, false);
      setModalTriggerExpanded(details, false);
      panel.removeAttribute('role');
      panel.removeAttribute('aria-modal');
      syncPageScrollLock();
    }
  };

  function syncModalSemantics() {
    document.querySelectorAll('details[data-thb-modal-details]').forEach((details) => {
      const panel = details.querySelector('[data-thb-modal-panel]');
      if (!(panel instanceof HTMLElement)) return;
      if (details.matches('.thb-header__mobile-menu') && details.open && !isModalDetails(details)) {
        const header = details.closest('.thb-header');
        // CSS can hide the mobile trigger before resize handlers run, dropping
        // focus onto BODY. Keep that V2 breakpoint transition keyboard-safe.
        const lostV2Focus = header?.classList.contains('thb-header--v2') &&
          (document.activeElement === document.body || document.activeElement === document.documentElement);
        const focusWasInside = details.contains(document.activeElement) || lostV2Focus;
        closeModalDetails(details, false);
        if (focusWasInside) {
          const logo = details.closest('.thb-header')?.querySelector('.thb-header__logo');
          if (logo instanceof HTMLElement) logo.focus({ preventScroll: true });
        }
      }
      if (isModalDetails(details)) {
        if (details.open) {
          setModalTriggerExpanded(details, true);
          panel.setAttribute('role', 'dialog');
          panel.setAttribute('aria-modal', 'true');
          setOutsideModalInert(details, true);
        } else {
          setModalTriggerExpanded(details, false);
          setOutsideModalInert(details, false);
          panel.removeAttribute('role');
          panel.removeAttribute('aria-modal');
        }
      } else {
        setModalTriggerExpanded(details, details.open);
        setOutsideModalInert(details, false);
        panel.removeAttribute('role');
        panel.removeAttribute('aria-modal');
      }
    });

    const isMobileHeader = window.matchMedia('(max-width: 1199px)').matches;
    document.querySelectorAll('.thb-header').forEach((header) => {
      const hiddenDetails = isMobileHeader
        ? header.querySelectorAll('.thb-header__nav-details[open]')
        : header.querySelectorAll('.thb-header__mobile-submenu[open]');

      hiddenDetails.forEach((details) => {
        const focusWasInside = details.contains(document.activeElement);
        details.removeAttribute('open');
        if (!focusWasInside) return;
        const visibleTarget = isMobileHeader
          ? header.querySelector('.thb-header__mobile-trigger')
          : header.querySelector('.thb-header__nav-link, .thb-header__logo');
        if (visibleTarget instanceof HTMLElement) visibleTarget.focus({ preventScroll: true });
      });
    });
    syncPageScrollLock();
  }

  function syncFooterMenus(resetForBreakpoint = false) {
    const isDesktop = window.matchMedia('(min-width: 769px)').matches;
    document.querySelectorAll('details[data-thb-footer-menu]').forEach((details) => {
      const summary = details.querySelector(':scope > summary');
      if (!(summary instanceof HTMLElement)) return;
      if (isDesktop) {
        details.open = true;
        summary.tabIndex = -1;
        summary.setAttribute('aria-disabled', 'true');
      } else {
        if (resetForBreakpoint || details.dataset.thbFooterReady !== 'true') details.open = false;
        summary.removeAttribute('tabindex');
        summary.removeAttribute('aria-disabled');
      }
      details.dataset.thbFooterReady = 'true';
    });
  }

  function observeFooterBreakpoint() {
    if (footerMenuMediaQuery) return;
    footerMenuMediaQuery = window.matchMedia('(min-width: 769px)');
    const handleChange = () => syncFooterMenus(true);
    if ('addEventListener' in footerMenuMediaQuery) {
      footerMenuMediaQuery.addEventListener('change', handleChange);
    } else {
      footerMenuMediaQuery.addListener(handleChange);
    }
  }

  function observeFooterOfferBoundary(excludedScope) {
    footerOfferObserver?.disconnect();
    footerOfferObserver = undefined;
    const root = document.documentElement;
    root.removeAttribute('data-thb-footer-in-view');
    root.toggleAttribute('data-thb-footer-observer-ready', 'IntersectionObserver' in window);
    if (!('IntersectionObserver' in window)) return;

    const isIncluded = (element) => !excludedScope?.contains(element);
    const footers = Array.from(document.querySelectorAll('.thb-footer')).filter(isIncluded);
    const offers = Array.from(document.querySelectorAll('.thb-special-offer')).filter(isIncluded);
    if (footers.length === 0 || offers.length === 0) return;

    const intersections = new Map(footers.map((footer) => {
      const rect = footer.getBoundingClientRect();
      return [footer, rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth];
    }));
    const syncBoundary = () => root.toggleAttribute('data-thb-footer-in-view', Array.from(intersections.values()).some(Boolean));
    const observer = new IntersectionObserver((entries) => {
      if (footerOfferObserver !== observer) return;
      entries.forEach((entry) => {
        intersections.set(entry.target, entry.isIntersecting);
      });
      syncBoundary();
    });
    footerOfferObserver = observer;
    syncBoundary();
    footers.forEach((footer) => observer.observe(footer));
  }

  const getRailItems = (rail) => {
    const markedItems = Array.from(rail.querySelectorAll(':scope > [data-thb-rail-item]'));
    const items = markedItems.length > 0 ? markedItems : Array.from(rail.children);
    return items.filter((item) => item instanceof HTMLElement);
  };

  const findRailControls = (rail) => {
    if (!rail.id) return;
    return Array.from(document.querySelectorAll('[data-thb-rail-controls]')).find((controls) => (
      Array.from(controls.querySelectorAll('[aria-controls]'))
        .some((button) => button.getAttribute('aria-controls') === rail.id)
    ));
  };

  const getRailMetrics = (rail) => {
    const items = getRailItems(rail);
    const styles = window.getComputedStyle(rail);
    const isRtl = styles.direction === 'rtl';
    const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
    const currentScroll = Math.min(maxScroll, Math.max(0, isRtl ? -rail.scrollLeft : rail.scrollLeft));
    const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
    const paddingStart = Number.parseFloat(isRtl ? styles.paddingRight : styles.paddingLeft) || 0;
    const paddingEnd = Number.parseFloat(isRtl ? styles.paddingLeft : styles.paddingRight) || 0;
    const availableWidth = Math.max(1, rail.clientWidth - paddingStart - paddingEnd);
    let occupiedWidth = 0;
    let pageSize = 0;

    items.some((item) => {
      const itemWidth = item.getBoundingClientRect().width;
      const nextWidth = occupiedWidth + (pageSize > 0 ? gap : 0) + itemWidth;
      if (pageSize > 0 && nextWidth > availableWidth + 1) return true;
      occupiedWidth = nextWidth;
      pageSize += 1;
      return false;
    });

    pageSize = Math.max(1, pageSize);
    const expectedPages = maxScroll > 1 ? Math.max(2, Math.ceil(items.length / pageSize)) : 1;
    const railRect = rail.getBoundingClientRect();
    const contentStart = isRtl ? railRect.right - paddingStart : railRect.left + paddingStart;
    const positions = [];

    for (let pageIndex = 0; pageIndex < expectedPages; pageIndex += 1) {
      let position = 0;
      if (pageIndex === expectedPages - 1) {
        position = maxScroll;
      } else if (pageIndex > 0) {
        const targetItem = items[Math.min(items.length - 1, pageIndex * pageSize)];
        if (targetItem) {
          const itemRect = targetItem.getBoundingClientRect();
          const itemStart = isRtl ? itemRect.right : itemRect.left;
          const distance = isRtl ? contentStart - itemStart : itemStart - contentStart;
          position = Math.min(maxScroll, Math.max(0, currentScroll + distance));
        }
      }
      if (positions.length === 0 || Math.abs(positions[positions.length - 1] - position) > 1) positions.push(position);
    }

    if (maxScroll > 1 && Math.abs((positions[positions.length - 1] || 0) - maxScroll) > 1) positions.push(maxScroll);
    if (positions.length > 2) {
      const finalStep = positions[positions.length - 1] - positions[positions.length - 2];
      const minimumUsefulStep = Math.max(32, availableWidth * .1);
      if (finalStep < minimumUsefulStep) positions.splice(positions.length - 2, 1);
    }
    return { currentScroll, isRtl, maxScroll, positions };
  };

  const getActiveRailPage = (metrics) => {
    let activePage = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;
    metrics.positions.forEach((position, index) => {
      const distance = Math.abs(position - metrics.currentScroll);
      if (distance < nearestDistance) {
        activePage = index;
        nearestDistance = distance;
      }
    });
    return activePage;
  };

  const renderRailPages = (state, activePage, pageCount) => {
    if (!(state.pagination instanceof HTMLElement)) return;
    const signature = String(pageCount);
    if (state.pagination.dataset.thbRailPageSignature !== signature) {
      const fragment = document.createDocumentFragment();
      ['start', 'end'].forEach((position) => {
        const endpoint = document.createElement('span');
        endpoint.className = 'thb-rail-controls__page thb-rail-controls__page--endpoint';
        endpoint.dataset.thbRailPagePosition = position;
        fragment.append(endpoint);
      });
      const progress = document.createElement('span');
      progress.className = 'thb-rail-controls__page thb-rail-controls__page--progress';
      fragment.append(progress);
      state.pagination.replaceChildren(fragment);
      state.pagination.dataset.thbRailPageSignature = signature;
    }
    const progressRatio = pageCount > 1 ? activePage / (pageCount - 1) : 0;
    state.pagination.style.setProperty('--thb-rail-progress', String(Math.min(1, Math.max(0, progressRatio))));
  };

  const setRailButtonState = (button, disabled) => {
    if (!(button instanceof HTMLButtonElement)) return;
    button.disabled = disabled;
    button.setAttribute('aria-disabled', String(disabled));
  };

  const syncRailControls = (rail) => {
    const state = railStates.get(rail);
    if (!state) return;
    const metrics = getRailMetrics(rail);
    const hasOverflow = metrics.maxScroll > 1 && metrics.positions.length > 1;
    state.controls.hidden = !hasOverflow;

    if (!hasOverflow) {
      setRailButtonState(state.previous, true);
      setRailButtonState(state.next, true);
      state.pagination?.replaceChildren();
      if (state.pagination) {
        delete state.pagination.dataset.thbRailPageSignature;
        state.pagination.style.removeProperty('--thb-rail-progress');
      }
      if (state.status) state.status.textContent = '';
      delete rail.dataset.thbRailPage;
      delete rail.dataset.thbRailPages;
      state.activePage = -1;
      state.pageCount = 0;
      return;
    }

    const activePage = getActiveRailPage(metrics);
    const loops = rail.dataset.thbRailLoop === 'true';
    setRailButtonState(state.previous, !loops && metrics.currentScroll <= 1);
    setRailButtonState(state.next, !loops && metrics.currentScroll >= metrics.maxScroll - 1);
    renderRailPages(state, activePage, metrics.positions.length);
    rail.dataset.thbRailPage = String(activePage + 1);
    rail.dataset.thbRailPages = String(metrics.positions.length);

    if (state.status && (state.activePage !== activePage || state.pageCount !== metrics.positions.length)) {
      const template = state.controls.dataset.thbRailStatusTemplate;
      state.status.textContent = template
        ? template.replace('__index__', String(activePage + 1)).replace('__length__', String(metrics.positions.length))
        : '';
    }
    state.activePage = activePage;
    state.pageCount = metrics.positions.length;
  };

  const scheduleRailSync = (rail) => {
    const state = railStates.get(rail);
    if (!state || state.frame) return;
    state.frame = window.requestAnimationFrame(() => {
      state.frame = 0;
      syncRailControls(rail);
    });
  };

  const observeRailSizes = (rail) => {
    const state = railStates.get(rail);
    if (!state?.resizeObserver) return;
    state.resizeObserver.disconnect();
    state.resizeObserver.observe(rail);
    getRailItems(rail).forEach((item) => state.resizeObserver.observe(item));
  };

  const scrollRail = (button) => {
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    const railId = button.getAttribute('aria-controls');
    const rail = railId ? document.getElementById(railId) : undefined;
    const state = rail instanceof HTMLElement ? railStates.get(rail) : undefined;
    if (!(rail instanceof HTMLElement) || !state) return;
    const metrics = getRailMetrics(rail);
    if (metrics.positions.length < 2) return;

    const movesBackward = button.matches('[data-thb-rail-prev]');
    const activePage = getActiveRailPage(metrics);
    const lastPage = metrics.positions.length - 1;
    let targetPage = movesBackward ? activePage - 1 : activePage + 1;
    if (targetPage < 0 && rail.dataset.thbRailLoop === 'true') targetPage = lastPage;
    if (targetPage > lastPage && rail.dataset.thbRailLoop === 'true') targetPage = 0;
    if (targetPage < 0) return;
    if (targetPage > lastPage) return;

    const targetPosition = metrics.positions[targetPage];
    const left = metrics.isRtl ? -targetPosition : targetPosition;
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    if (typeof rail.scrollTo === 'function') {
      rail.scrollTo({ left, behavior });
    } else {
      rail.scrollLeft = left;
    }
    scheduleRailSync(rail);
  };

  const initializeRailControls = (rail) => {
    if (!(rail instanceof HTMLElement)) return;
    if (railStates.has(rail)) {
      scheduleRailSync(rail);
      return;
    }

    const controls = findRailControls(rail);
    const previous = controls?.querySelector(`[data-thb-rail-prev][aria-controls="${CSS.escape(rail.id)}"]`);
    const next = controls?.querySelector(`[data-thb-rail-next][aria-controls="${CSS.escape(rail.id)}"]`);
    if (!(controls instanceof HTMLElement) || !(previous instanceof HTMLButtonElement) || !(next instanceof HTMLButtonElement)) return;

    const controller = new AbortController();
    const state = {
      activePage: -1,
      controller,
      controls,
      frame: 0,
      focusFrame: 0,
      mutationObserver: undefined,
      next,
      pageCount: 0,
      pagination: controls.querySelector('[data-thb-rail-pages]'),
      previous,
      resizeObserver: undefined,
      status: controls.querySelector('[data-thb-rail-status]'),
    };
    railStates.set(rail, state);
    rail.dataset.thbControlsReady = 'true';
    controls.hidden = true;

    previous.addEventListener('click', () => scrollRail(previous), { signal: controller.signal });
    next.addEventListener('click', () => scrollRail(next), { signal: controller.signal });
    rail.addEventListener('focusin', (event) => {
      const focused = event.target;
      if (!(focused instanceof HTMLElement) || !focused.matches(':focus-visible')) return;
      if (!rail.matches('.thb-category-rail, .thb-product-rail')) return;
      const item = focused.closest('[data-thb-rail-item]');
      if (!(item instanceof HTMLElement) || item.parentElement !== rail) return;
      if (state.focusFrame) window.cancelAnimationFrame(state.focusFrame);
      state.focusFrame = window.requestAnimationFrame(() => {
        state.focusFrame = 0;
        if (document.activeElement !== focused) return;
        const bounds = rail.getBoundingClientRect();
        const itemBounds = item.getBoundingClientRect();
        const styles = window.getComputedStyle(rail);
        const paddingLeft = Number.parseFloat(styles.paddingLeft) || 0;
        const paddingRight = Number.parseFloat(styles.paddingRight) || 0;
        if (itemBounds.left >= bounds.left + paddingLeft - 1 && itemBounds.right <= bounds.right - paddingRight + 1) return;
        const metrics = getRailMetrics(rail);
        const distance = metrics.isRtl
          ? bounds.right - paddingRight - itemBounds.right
          : itemBounds.left - bounds.left - paddingLeft;
        const position = Math.min(metrics.maxScroll, Math.max(0, metrics.currentScroll + distance));
        rail.scrollTo({ left: metrics.isRtl ? -position : position, behavior: 'instant' });
        scheduleRailSync(rail);
      });
    }, { signal: controller.signal });
    rail.addEventListener('scroll', () => scheduleRailSync(rail), { passive: true, signal: controller.signal });
    rail.addEventListener('load', () => scheduleRailSync(rail), { capture: true, signal: controller.signal });
    window.addEventListener('resize', () => scheduleRailSync(rail), { passive: true, signal: controller.signal });

    if ('ResizeObserver' in window) {
      state.resizeObserver = new ResizeObserver(() => scheduleRailSync(rail));
      observeRailSizes(rail);
    }
    if ('MutationObserver' in window) {
      state.mutationObserver = new MutationObserver(() => {
        observeRailSizes(rail);
        scheduleRailSync(rail);
      });
      state.mutationObserver.observe(rail, { childList: true });
    }
    syncRailControls(rail);
  };

  const destroyRailControls = (rail) => {
    const state = railStates.get(rail);
    if (!state) return;
    state.controller.abort();
    state.resizeObserver?.disconnect();
    state.mutationObserver?.disconnect();
    if (state.frame) window.cancelAnimationFrame(state.frame);
    if (state.focusFrame) window.cancelAnimationFrame(state.focusFrame);
    state.controls.hidden = true;
    state.pagination?.replaceChildren();
    if (state.pagination) {
      delete state.pagination.dataset.thbRailPageSignature;
      state.pagination.style.removeProperty('--thb-rail-progress');
    }
    if (state.status) state.status.textContent = '';
    railStates.delete(rail);
    delete rail.dataset.thbControlsReady;
    delete rail.dataset.thbRailPage;
    delete rail.dataset.thbRailPages;
  };

  document.addEventListener('shopify:cart:lines-update', handleCartUpdate);
  document.addEventListener('shopify:cart:discount-update', handleCartUpdate);

  document.addEventListener('focusin', (event) => {
    const card = event.target;
    if (!(card instanceof HTMLElement) || !card.matches('.thb-brands a.thb-brand-card:focus-visible')) return;
    const row = card.closest('.thb-brand-grid');
    if (!(row instanceof HTMLElement) || row.scrollWidth <= row.clientWidth + 1) return;
    requestAnimationFrame(() => {
      if (document.activeElement !== card) return;
      card.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
    });
  });

  document.addEventListener('keydown', (event) => {
    if (!(event.target instanceof Element)) return;
    navigationKeyboardInput = true;
    const modalDetails = event.target.closest('details[data-thb-modal-details][open]');

    if (modalDetails instanceof HTMLDetailsElement && isModalDetails(modalDetails)) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        closeModalDetails(modalDetails);
        return;
      }

      if (event.key === 'Tab') {
        const panel = modalDetails.querySelector('[data-thb-modal-panel]');
        if (!(panel instanceof HTMLElement)) return;
        const focusables = modalFocusables(panel);
        if (focusables.length === 0) {
          event.preventDefault();
          panel.focus();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
      return;
    }

    if (event.key !== 'Escape') return;
    const focusedDetails = event.target.closest('.thb-header details[open]');
    if (!focusedDetails && (event.defaultPrevented || event.target.closest('dialog[open], [role="dialog"][aria-modal="true"]'))) return;
    const openDetails = focusedDetails || document.querySelector('.thb-header__nav-details[open]');
    if (!(openDetails instanceof HTMLDetailsElement)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const summary = openDetails.querySelector(':scope > summary');
    cancelNavigationClose(openDetails);
    openDetails.open = false;
    if (focusedDetails) summary?.focus();
  });

  document.addEventListener('toggle', (event) => {
    const opened = event.target;
    if (!(opened instanceof HTMLDetailsElement)) return;
    if (opened.matches('[data-thb-footer-menu]') && window.matchMedia('(min-width: 769px)').matches && !opened.open) {
      opened.open = true;
      return;
    }
    if (opened.matches('[data-thb-modal-details]')) handleModalToggle(opened);
    if (!opened.open || !opened.matches('[data-auto-close-details]')) return;

    let group = null;
    let selector = '';
    if (opened.matches('.thb-header__nav-details')) {
      group = opened.closest('.thb-header__nav-list');
      selector = '.thb-header__nav-details[data-auto-close-details][open]';
    } else if (opened.matches('.thb-header__mobile-submenu')) {
      group = opened.closest('.thb-header__mobile-list');
      selector = '.thb-header__mobile-submenu[data-auto-close-details][open]';
    }

    if (!group) return;
    group.querySelectorAll(selector).forEach((details) => {
      if (details !== opened) details.removeAttribute('open');
    });
  }, true);

  document.addEventListener('click', (event) => {
    const clicked = event.target;
    if (!(clicked instanceof Element)) return;

    const footerSummary = clicked.closest('summary');
    if (
      footerSummary?.parentElement?.matches('details[data-thb-footer-menu]')
      && window.matchMedia('(min-width: 769px)').matches
    ) {
      event.preventDefault();
      return;
    }

    const modalClose = clicked.closest('[data-thb-modal-close]');
    const modalDetails = modalClose?.closest('details[data-thb-modal-details][open]');
    if (modalDetails instanceof HTMLDetailsElement) {
      event.preventDefault();
      closeModalDetails(modalDetails);
      return;
    }

    document.querySelectorAll('.thb-header details[data-auto-close-details][open]').forEach((details) => {
      if (!details.contains(clicked)) details.removeAttribute('open');
    });
  });

  window.addEventListener('resize', () => {
    syncModalSemantics();
  }, { passive: true });

  window.addEventListener('pageshow', () => observeFooterOfferBoundary());

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeThb(), { once: true });
  } else {
    initializeThb();
  }

  document.addEventListener('shopify:section:load', (event) => initializeThb(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const blockId = event.detail?.blockId;
    if (!blockId) return;
    const categoryItem = document.querySelector(`[data-thb-category-block-id="${CSS.escape(blockId)}"]`);
    const categoryRail = categoryItem?.closest('[data-thb-rail]');
    if (categoryItem instanceof HTMLElement && categoryRail instanceof HTMLElement) {
      categoryItem.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
      scheduleRailSync(categoryRail);
    }
    document.querySelectorAll('[data-thb-announcement]').forEach((announcement) => {
      const state = announcementStates.get(announcement);
      const slideIndex = state?.slides.findIndex((slide) => slide.dataset.thbAnnouncementBlockId === blockId) ?? -1;
      if (slideIndex >= 0) selectAnnouncement(announcement, slideIndex);
    });
    document.querySelectorAll('[data-thb-lookbook]').forEach((lookbook) => {
      const hotspot = [...lookbook.querySelectorAll('[data-thb-look-hotspot]')]
        .find((candidate) => candidate.dataset.thbLookHotspot === blockId);
      if (hotspot) activateLookProduct(lookbook, blockId);
    });
  });
  document.addEventListener('shopify:section:unload', (event) => {
    observeFooterOfferBoundary(event.target);
    event.target.querySelectorAll('details[data-thb-modal-details]').forEach((details) => {
      setOutsideModalInert(details, false);
    });
    event.target.querySelectorAll('[data-thb-countdown]').forEach(clearCountdown);
    event.target.querySelectorAll('[data-thb-rail]').forEach(destroyRailControls);
    event.target.querySelectorAll('[data-thb-lookbook]').forEach(destroyLookbook);
    event.target.querySelectorAll('[data-thb-home]').forEach(destroyImageDialog);
    event.target.querySelectorAll('[data-thb-copy-code]').forEach(destroyCopyCode);
    event.target.querySelectorAll('[data-thb-announcement]').forEach(destroyAnnouncement);
    requestAnimationFrame(syncModalSemantics);
  });
})();
